import { composicaoDoValor } from './serviceValue';
import { dataDeCompetencia, noPeriodo } from './competencia';
import { BILLING_MODELS } from '../../constants';

/**
 * DE ONDE VEM CADA REAL.
 *
 * O relatório dizia quanto o laboratório realizou e quanto disso virou fatura, e
 * paravamos aí. "Realizado: R$ 47.320" não deixa ninguém decidir nada: não diz se
 * veio de dois clientes ou de vinte, se é embrião transferido ou quilometragem
 * repassada, nem se o mês foi melhor que o anterior por preço ou por volume.
 *
 * Então o mesmo número é aberto por quatro eixos, e os quatro somam o mesmo
 * total. Somar diferente do total é o defeito que mata um painel: quem lê para de
 * confiar nos dois números.
 *
 *   COMPOSIÇÃO   o que dentro do serviço gerou o real — a base do modelo, o
 *                congelamento, cada repasse. É a anatomia do preço.
 *   CLIENTE      de quem veio. É a pergunta comercial.
 *   MODELO       por qual regra foi cobrado. Diz se o laboratório vive de
 *                prenhez, de embrião transferido ou de pacote.
 *   MÊS          quando. É a única que responde "está melhorando?".
 *
 * Todas usam `dataDeCompetencia`, a mesma do livro do realizado: a receita é do
 * mês em que o trabalho fechou, não do mês em que a fatura saiu.
 */

const centavos = (n) => Math.round((Number(n) || 0) * 100);
const reais = (c) => c / 100;

/** Os serviços do recorte, com a composição de cada um já calculada. */
const doPeriodo = (services = [], { de, ate } = {}) =>
  (services || [])
    .filter(s => noPeriodo(dataDeCompetencia(s), { de, ate }))
    .map(s => ({ service: s, composicao: composicaoDoValor(s) }))
    .filter(x => x.composicao.total > 0);

/**
 * Ordena por valor e corta a cauda num "outros".
 *
 * Vinte barras num gráfico não se comparam — vira uma lista deitada. As dez
 * maiores respondem a pergunta, e o resto precisa continuar somando, senão o
 * gráfico passa a discordar do total que está logo acima dele.
 */
const comOutros = (linhas, limite) => {
  const ordenadas = [...linhas].sort((a, b) => b.valor - a.valor || a.rotulo.localeCompare(b.rotulo));
  if (!limite || ordenadas.length <= limite) return ordenadas;

  const cabeca = ordenadas.slice(0, limite);
  const cauda = ordenadas.slice(limite);
  return [...cabeca, {
    chave: '__outros',
    rotulo: `Outros ${cauda.length}`,
    valor: reais(cauda.reduce((t, l) => t + centavos(l.valor), 0)),
    servicos: cauda.reduce((t, l) => t + (l.servicos || 0), 0),
  }];
};

/** Acrescenta a fatia de cada linha no total. */
const comFatia = (linhas) => {
  const total = linhas.reduce((t, l) => t + centavos(l.valor), 0);
  return linhas.map(l => ({
    ...l,
    // Zero no denominador devolve zero, e não NaN escrito na tela.
    pct: total > 0 ? Math.round((centavos(l.valor) / total) * 1000) / 10 : 0,
  }));
};

const agrupa = (itens, chaveDe, rotuloDe) => {
  const mapa = new Map();
  for (const item of itens) {
    const chave = chaveDe(item) ?? '—';
    if (!mapa.has(chave)) {
      mapa.set(chave, { chave, rotulo: rotuloDe(item), valor: 0, servicos: 0, _c: 0 });
    }
    const g = mapa.get(chave);
    g._c += centavos(item.composicao.total);
    g.servicos += 1;
  }
  return [...mapa.values()].map(({ _c, ...g }) => ({ ...g, valor: reais(_c) }));
};

/**
 * A ANATOMIA DO PREÇO: base do modelo, congelamento e cada repasse.
 *
 * É a resposta mais literal a "de onde vem cada real", e a que não existia em
 * nenhuma tela: o repasse de quilometragem entrava no valor do serviço e
 * desaparecia dentro dele. Um laboratório que descobre que 8% do que realizou é
 * frete repassado negocia o contrato de outra forma.
 */
export const composicaoDaReceita = (services = [], recorte = {}) => {
  const itens = doPeriodo(services, recorte);
  const mapa = new Map();

  const soma = (chave, rotulo, valor) => {
    if (centavos(valor) === 0) return;
    if (!mapa.has(chave)) mapa.set(chave, { chave, rotulo, valor: 0, _c: 0 });
    mapa.get(chave)._c += centavos(valor);
  };

  for (const { composicao } of itens) {
    soma('servico', 'Serviço', composicao.base);
    composicao.congelamento.forEach(l => soma(`cong:${l.tipo}`, l.rotulo, l.subtotal));
    composicao.repasses.forEach(l => soma(`rep:${l.type}`, `Repasse · ${l.grupo || l.label}`, l.value));
  }

  const linhas = [...mapa.values()].map(({ _c, ...g }) => ({ ...g, valor: reais(_c) }));
  return comFatia(comOutros(linhas));
};

/** De quem vem. `limite` corta a cauda num "outros" que continua somando. */
export const receitaPorCliente = (services = [], recorte = {}, limite = 10) =>
  comFatia(comOutros(
    agrupa(doPeriodo(services, recorte),
      x => x.service.client_id || x.service.client_name,
      x => x.service.client_name || 'Sem cliente'),
    limite,
  ));

const ROTULO_DO_MODELO = {
  [BILLING_MODELS.POR_TE]: 'Por transferência',
  [BILLING_MODELS.POR_PRENHEZ]: 'Por prenhez',
  [BILLING_MODELS.POR_EMBRIAO]: 'Por embrião',
  [BILLING_MODELS.PACOTE_COMPLETO]: 'Pacote completo',
  [BILLING_MODELS.PERSONALIZADO]: 'Personalizado',
  [BILLING_MODELS.DIARIA]: 'Diária',
  [BILLING_MODELS.SEM_COBRANCA]: 'Sem cobrança',
};

/** Por qual regra foi cobrado. */
export const receitaPorModelo = (services = [], recorte = {}) =>
  comFatia(comOutros(
    agrupa(doPeriodo(services, recorte),
      x => x.service.billing_model || 'indefinido',
      x => ROTULO_DO_MODELO[x.service.billing_model] || 'Modelo não informado'),
  ));

/**
 * Mês a mês, em ordem cronológica.
 *
 * Único eixo que NÃO se ordena por valor: série temporal fora de ordem não é
 * série. E sem mês vazio no meio — o mês em que nada fechou é informação, e
 * pular dele para o seguinte esconde a queda.
 */
export const receitaPorMes = (services = [], recorte = {}) => {
  const itens = doPeriodo(services, recorte);
  if (itens.length === 0) return [];

  const mapa = new Map();
  for (const item of itens) {
    const mes = String(dataDeCompetencia(item.service)).slice(0, 7);
    mapa.set(mes, (mapa.get(mes) || 0) + centavos(item.composicao.total));
  }

  const meses = [...mapa.keys()].sort();
  const preencheAte = (a, b) => {
    const saida = [];
    let [ano, mes] = a.split('-').map(Number);
    for (;;) {
      const atual = `${ano}-${String(mes).padStart(2, '0')}`;
      saida.push(atual);
      if (atual === b) break;
      mes += 1;
      if (mes > 12) { mes = 1; ano += 1; }
      // Trava de segurança: recorte maluco não vira laço infinito.
      if (saida.length > 240) break;
    }
    return saida;
  };

  return preencheAte(meses[0], meses[meses.length - 1]).map(mes => ({
    chave: mes,
    rotulo: rotuloDoMes(mes),
    valor: reais(mapa.get(mes) || 0),
  }));
};

/** "2026-09" vira "set/26", que é como se lê num eixo estreito. */
export const rotuloDoMes = (mes) => {
  const [ano, m] = String(mes || '').split('-').map(Number);
  if (!ano || !m) return String(mes || '');
  const nome = new Date(Date.UTC(ano, m - 1, 1))
    .toLocaleDateString('pt-BR', { month: 'short', timeZone: 'UTC' })
    .replace('.', '');
  return `${nome}/${String(ano).slice(2)}`;
};

/**
 * O TICKET: quanto vale um serviço, em média, e onde ele se concentra.
 *
 * Média sozinha engana quando um serviço de R$ 6.600 divide a lista com nove de
 * R$ 270, e é exatamente a distribuição do laboratório. Por isso vem com a
 * MEDIANA ao lado: as duas juntas dizem se o faturamento depende de poucos
 * grandes — que é o risco que ninguém vê num número médio.
 */
export const ticketDoPeriodo = (services = [], recorte = {}) => {
  const valores = doPeriodo(services, recorte)
    .map(x => centavos(x.composicao.total))
    .sort((a, b) => a - b);

  if (valores.length === 0) {
    return { servicos: 0, total: 0, media: 0, mediana: 0, maior: 0, concentracao: 0 };
  }

  const total = valores.reduce((t, v) => t + v, 0);
  const meio = Math.floor(valores.length / 2);
  const mediana = valores.length % 2
    ? valores[meio]
    : Math.round((valores[meio - 1] + valores[meio]) / 2);

  // Quanto do total está nos três maiores. É a leitura de risco: 70% em três
  // serviços é um mês que depende de três telefonemas.
  const tresMaiores = valores.slice(-3).reduce((t, v) => t + v, 0);

  return {
    servicos: valores.length,
    total: reais(total),
    media: reais(Math.round(total / valores.length)),
    mediana: reais(mediana),
    maior: reais(valores[valores.length - 1]),
    concentracao: total > 0 ? Math.round((tresMaiores / total) * 1000) / 10 : 0,
  };
};
